import { Controller } from "@hotwired/stimulus"

// Fills the hidden address fields from a Google Place selected in the
// Form::GoogleAutocompleteInput component. Mirrors
// onboarding_general_info_controller.js, trimmed to the address concern
// for the back-office caterer listing creation form.
export default class extends Controller {
  static targets = [
    "googlePlaceIdInput",
    "lngInput",
    "latInput",
    "addressInput",
    "zipCodeInput",
    "cityInput",
    "countryCodeInput",
    "pictureInput",
    "pictureList"
  ]
  static outlets = ["form--google-autocomplete-input"]

  formGoogleAutocompleteInputOutletConnected(outlet) {
    outlet.selectGooglePlace = this.selectGooglePlace.bind(this)
  }

  selectGooglePlace(placeData) {
    this.placeData = placeData
    this.formGoogleAutocompleteInputOutlet.inputTarget.value = this.placeData.name
    this.fillFormInputsWithPlaceData()
  }

  fillFormInputsWithPlaceData() {
    this.zipCodeInputTarget.value = this.findAddressComponent("postal_code")?.long_name || ""
    this.cityInputTarget.value = this.findAddressComponent("locality")?.long_name || ""
    this.countryCodeInputTarget.value = this.findAddressComponent("country")?.short_name || ""
    this.googlePlaceIdInputTarget.value = this.placeData.place_id
    this.latInputTarget.value = this.placeData.geometry.location.lat()
    this.lngInputTarget.value = this.placeData.geometry.location.lng()
    this.addressInputTarget.value = this.formattedAddress()
  }

  formattedAddress() {
    const streetNumber = this.findAddressComponent("street_number")?.long_name || ""
    const route = this.findAddressComponent("route")?.long_name || ""
    return `${streetNumber} ${route}`.trim()
  }

  findAddressComponent(type) {
    return this.placeData.address_components?.find(c => c.types.includes(type))
  }

  // Picture dropzone. Files are uploaded to storage by ActiveStorage direct
  // upload when the form is submitted; this only opens the picker, accepts
  // drops, and previews the selection. No auto-submit.
  openPictureBrowser() {
    this.pictureInputTarget.click()
  }

  acceptDraggedPictures(event) {
    event.preventDefault()
  }

  handleDroppedPictures(event) {
    event.preventDefault()
    this.addPictures(event.dataTransfer.files)
  }

  // A native file input replaces its FileList on every pick, so accumulate each
  // selection/drop into one DataTransfer and write it back — lets the admin add
  // several photos across multiple picks and submit them all at once.
  selectPictures() {
    this.addPictures(this.pictureInputTarget.files)
  }

  addPictures(fileList) {
    Array.from(fileList).forEach(file => this.pictureBag.items.add(file))
    this.pictureInputTarget.files = this.pictureBag.files
    this.renderPicturePreviews()
  }

  removePicture(event) {
    this.pictureBag.items.remove(Number(event.currentTarget.dataset.index))
    this.pictureInputTarget.files = this.pictureBag.files
    this.renderPicturePreviews()
  }

  renderPicturePreviews() {
    this.pictureListTarget.innerHTML = ""

    Array.from(this.pictureBag.files).forEach((file, index) => {
      const wrapper = document.createElement("div")
      wrapper.className = "position-relative"
      wrapper.style.cssText = "width: 96px; height: 96px;"
      wrapper.innerHTML = `
        <img src="${URL.createObjectURL(file)}" class="rounded w-100 h-100" style="object-fit: cover;" />
        <button type="button"
                class="btn btn-sm btn-danger position-absolute p-0"
                style="top: 2px; right: 2px; width: 20px; height: 20px; line-height: 1;"
                data-index="${index}"
                data-action="caterer-listing-form#removePicture">&times;</button>
      `
      this.pictureListTarget.appendChild(wrapper)
    })
  }

  get pictureBag() {
    this._pictureBag ||= new DataTransfer()
    return this._pictureBag
  }
}
